import { Box, Button, Typography, Fade } from '@mui/material';

/**
 * CaidaOpportunityButtons - Botones para las oportunidades de Caída/Contracaída/San Vicente
 * 
 * Se muestran cuando el jugador humano tiene la oportunidad de hacer una caída,
 * es decir, cuando otro jugador ha soltado una carta y el humano tiene una del mismo valor.
 * 
 * Opciones:
 * - Caer: Jugar la carta del mismo valor para hacer la caída
 * - Pasar: No hacer la caída y dejar que el turno continúe normalmente
 */
const CaidaOpportunityButtons = ({ 
    visible = false,
    caidaType = 'caida', // 'caida', 'contra', 'san_vicente'
    cardValue = null, // Valor de la carta que puede caer
    onDecision, // (decision: 'caer' | 'pasar') => void
    disabled = false,
    playerName = 'Jugador',
    timeRemaining = null, // Tiempo restante para decidir (opcional)
}) => {
    if (!visible) {
        return null;
    }

    const getTypeLabel = () => {
        switch (caidaType) {
            case 'contra':
                return '¡Contracaída!';
            case 'san_vicente':
                return '¡San Vicente!';
            case 'caida':
            default:
                return '¡Caída!';
        }
    };

    const getTypePoints = () => {
        switch (caidaType) {
            case 'contra':
                return '+3 puntos';
            case 'san_vicente':
                return '+4 puntos';
            case 'caida':
            default:
                return '+1 punto';
        }
    };

    const getCardName = (value) => {
        const names = {
            1: 'As',
            2: 'Dos',
            3: 'Tres',
            4: 'Cuatro',
            5: 'Cinco',
            6: 'Seis',
            7: 'Siete',
            10: 'Sota',
            11: 'Caballo',
            12: 'Rey'
        };
        return names[value] || value?.toString() || '';
    };

    const caerButtonStyle = {
        fontWeight: 'bold',
        textTransform: 'none',
        fontSize: '1.1rem',
        padding: '12px 24px',
        borderRadius: '12px',
        minWidth: '140px',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.2s ease',
        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
        color: 'white',
        animation: 'pulse 1.5s infinite',
        '@keyframes pulse': {
            '0%': { boxShadow: '0 4px 15px rgba(231, 76, 60, 0.4)' },
            '50%': { boxShadow: '0 4px 25px rgba(231, 76, 60, 0.8)' },
            '100%': { boxShadow: '0 4px 15px rgba(231, 76, 60, 0.4)' },
        },
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(231, 76, 60, 0.6)',
            background: 'linear-gradient(135deg, #ec7063 0%, #e74c3c 100%)',
        },
        '&:disabled': {
            opacity: 0.5,
            animation: 'none',
        }
    };

    const pasarButtonStyle = {
        fontWeight: 'bold',
        textTransform: 'none',
        fontSize: '0.9rem',
        padding: '8px 16px',
        borderRadius: '10px',
        minWidth: '100px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s ease',
        background: 'rgba(100, 100, 100, 0.8)',
        color: 'white',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
            background: 'rgba(120, 120, 120, 0.9)',
        },
        '&:disabled': {
            opacity: 0.5,
        }
    };

    return (
        <Fade in={visible}>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1.5,
                    zIndex: 1000,
                    maxWidth: '320px',
                }}
            >
                {/* Header con información */}
                <Box
                    sx={{
                        background: 'rgba(0, 0, 0, 0.9)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '14px',
                        padding: '14px 18px',
                        boxShadow: '0 4px 20px rgba(231, 76, 60, 0.3)',
                        border: '2px solid rgba(231, 76, 60, 0.5)',
                    }}
                >
                    <Typography
                        sx={{
                            color: '#e74c3c',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            textShadow: '0 0 10px rgba(231, 76, 60, 0.5)',
                        }}
                    >
                        {getTypeLabel()} - {getTypePoints()}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '0.85rem',
                            textAlign: 'center',
                            marginTop: '6px',
                        }}
                    >
                        {playerName}, tienes un {getCardName(cardValue)}
                    </Typography>
                    {timeRemaining !== null && (
                        <Typography
                            sx={{
                                color: timeRemaining <= 3 ? '#e74c3c' : 'rgba(255, 255, 255, 0.6)',
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                marginTop: '4px',
                            }}
                        >
                            Tiempo: {timeRemaining}s
                        </Typography>
                    )}
                </Box>

                {/* Botones de decisión */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1.5,
                        alignItems: 'center',
                    }}
                >
                    <Button
                        onClick={() => onDecision('pasar')}
                        disabled={disabled}
                        sx={pasarButtonStyle}
                    >
                        Pasar
                    </Button>
                    <Button
                        onClick={() => onDecision('caer')}
                        disabled={disabled}
                        sx={caerButtonStyle}
                    >
                        ¡CAER!
                    </Button>
                </Box>
            </Box>
        </Fade>
    );
};

export default CaidaOpportunityButtons;
